// frontend/src/widgets/FinancialSummaryWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-16
// Updated:      2026-09-09 08:29 BST
//
// Description:
//   Financial Summary widget (Opportunities).
//   Uses REAL data from /api/opportunities and provides filterable pipeline metrics.
//
//   Key rules:
//     • Uses shared Axios instance (api) ONLY.
//     • Never supplies tenant_id (server authoritative).
//     • Degrades gracefully if optional endpoints (sales regions) do not exist.
//
// Features:
//   • Filters: status, stage, owner (sales-only), expected close date range, search.
//   • KPIs: total pipeline, weighted pipeline, open counts + amounts.
//   • Breakdowns: by stage, by status, by owner.
//   • Region-aware (By Region) if backend endpoints are available:
//       - GET /api/sales/regions
//       - GET /api/sales/resources/{resource_id}/regions   (optional)
//       - GET /api/resources/{resource_id}/sales-regions   (optional)
//     If not available, Region filter + breakdown will hide automatically.
//
// Mobile Responsiveness (2026-09-09):
//   • Preserves the existing opportunity, owner and optional-region data logic.
//   • Stacks all filter groups to one column on narrow screens.
//   • Makes Reset / Refresh controls full-width and touch-friendly on mobile.
//   • Converts the three KPI cards to a single-column mobile flow.
//   • Stacks Stage / Status breakdown panels on mobile.
//   • Converts Stage, Status, Owner and Region tables to labelled cards below
//     768px while preserving normal desktop tables at md and above.
//   • Uses natural widget height and visible overflow on mobile so lower
//     breakdown sections are not clipped.
//   • Allows owner names, region text, currency values and errors to wrap safely.
//   • Extends the existing portal tooltip to touch input and constrains it to
//     the available mobile viewport.
//
// Operational Notes (2026-09-09):
//   • Existing API routes, fallback requests, filtering and calculations are unchanged.
//   • No tenant_id is supplied by this widget.
//   • No additional polling or background refresh behaviour is introduced.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isNil = (v) => v === null || v === undefined;

const toNumber = (v) => {
  if (isNil(v)) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const normLower = (v) => String(v || "").trim().toLowerCase();

const resourceLabel = (r) => {
  if (!r) return "";
  const first = r.first_name || r.firstName || "";
  const last = r.last_name || r.lastName || "";
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const email = r.email || "";
  if (email) return email;
  const job = r.job_title || r.jobTitle || "";
  if (job) return job;
  const role = r.system_role || r.systemRole || "";
  if (role) return role;
  const ident = r.identifier || "";
  if (ident) return ident;
  const id = toInt(r.id);
  return id ? `Resource #${id}` : "Resource";
};

const moneyFmt = (n) => {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return "0";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(x);
  } catch {
    return `£${Math.round(x).toLocaleString()}`;
  }
};

const pctFmt = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return `${Math.round(x)}%`;
};

const isoDateOnly = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const dateNum = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const s = String(yyyyMmDd).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  return Number(s.replaceAll("-", ""));
};

const sumBy = (arr, getKey, getValue) => {
  const m = new Map();
  (arr || []).forEach((it) => {
    const k = getKey(it);
    const v = getValue(it);
    const cur = m.get(k) || 0;
    m.set(k, cur + v);
  });
  return m;
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function TooltipPortal({ open, x, y, text, theme }) {
  if (!open) return null;

  const bg = theme?.primary_color || "#111827";
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 1200;
  const tooltipMaxWidth = Math.max(
    160,
    Math.min(360, viewportWidth - 16)
  );

  const style = {
    position: "fixed",
    left: 0,
    top: 0,
    transform: `translate(${x}px, ${y}px)`,
    zIndex: 2147483647,
    pointerEvents: "none",
    background: bg,
    color: "#fff",
    padding: "10px 12px",
    borderRadius: 12,
    boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.25,
    maxWidth: tooltipMaxWidth,
    overflowWrap: "anywhere",
    whiteSpace: "pre-line",
  };

  return ReactDOM.createPortal(<div style={style}>{text}</div>, document.body);
}

export default function FinancialSummaryWidget() {
  const { theme } = useTheme();

  // data
  const [opps, setOpps] = useState([]);
  const [oppsLoading, setOppsLoading] = useState(false);
  const [oppsErr, setOppsErr] = useState("");

  const [salesOwners, setSalesOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersErr, setOwnersErr] = useState("");

  // Optional: sales regions support (best-effort)
  const [regionsSupported, setRegionsSupported] = useState(false);
  const [regions, setRegions] = useState([]);
  const [regionMapByResource, setRegionMapByResource] = useState({});
  const [regionsLoading, setRegionsLoading] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [stage, setStage] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [regionId, setRegionId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // tooltip (portal-to-body, unclipped, multiline)
  const [tipOpen, setTipOpen] = useState(false);
  const [tipText, setTipText] = useState("");
  const [tipXY, setTipXY] = useState({ x: 0, y: 0 });
  const tipHideTimer = useRef(null);

  const hideTip = useCallback(() => {
    if (tipHideTimer.current) {
      window.clearTimeout(tipHideTimer.current);
      tipHideTimer.current = null;
    }
    setTipOpen(false);
    setTipText("");
  }, []);

  const showTip = useCallback((text, evt) => {
    if (!text) return;
    if (tipHideTimer.current) {
      window.clearTimeout(tipHideTimer.current);
      tipHideTimer.current = null;
    }

    const margin = 12;
    const touch =
      evt?.touches?.[0] ||
      evt?.changedTouches?.[0] ||
      null;
    const vx = touch?.clientX ?? evt?.clientX ?? 0;
    const vy = touch?.clientY ?? evt?.clientY ?? 0;

    const approxW = clamp((String(text).length || 40) * 6.4, 160, 360);
    const approxLines = String(text).split("\n").length;
    const approxH = clamp(approxLines * 18 + 20, 44, 160);

    const x = clamp(vx + margin, 8, window.innerWidth - approxW - 8);
    const y = clamp(vy + margin, 8, window.innerHeight - approxH - 8);

    setTipXY({ x, y });
    setTipText(text);
    setTipOpen(true);
  }, []);

  const moveTip = useCallback(
    (evt) => {
      if (!tipOpen) return;
      const margin = 12;
      const touch =
        evt?.touches?.[0] ||
        evt?.changedTouches?.[0] ||
        null;
      const vx = touch?.clientX ?? evt?.clientX ?? 0;
      const vy = touch?.clientY ?? evt?.clientY ?? 0;

      const approxW = clamp((String(tipText).length || 40) * 6.4, 160, 360);
      const approxLines = String(tipText).split("\n").length;
      const approxH = clamp(approxLines * 18 + 20, 44, 160);

      const x = clamp(vx + margin, 8, window.innerWidth - approxW - 8);
      const y = clamp(vy + margin, 8, window.innerHeight - approxH - 8);
      setTipXY({ x, y });
    },
    [tipOpen, tipText]
  );

  useEffect(() => {
    if (!tipOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") hideTip();
    };
    const onScroll = () => hideTip();
    const onBlur = () => hideTip();

    window.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", onBlur);
    };
  }, [tipOpen, hideTip]);

  const wrap = useMemo(
    () => ({
      background: theme?.card_bg || "#fff",
      borderRadius: 14,
      padding: 14,
      boxShadow: theme?.card_shadow || "0 1px 2px rgba(0,0,0,0.06)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      boxSizing: "border-box",
    }),
    [theme]
  );

  const label = useMemo(() => ({ fontSize: 12, opacity: 0.75, fontWeight: 900 }), []);

  const input = useMemo(
    () => ({
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      borderRadius: 10,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: "10px 12px",
      fontSize: 12,
      outline: "none",
      background: "#fff",
    }),
    []
  );

  // IMPORTANT: NO question-mark cursor. Keep it clean.
  const labelHover = useMemo(
    () => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "default", // <-- NO help cursor
      userSelect: "none",
      width: "fit-content",
      maxWidth: "100%",
    }),
    []
  );

  const pill = useMemo(
    () => ({
      borderRadius: 999,
      border: `1px solid ${theme?.accent_color || "#ff7a00"}`,
      color: theme?.accent_color || "#ff7a00",
      padding: "6px 10px",
      fontSize: 11,
      fontWeight: 900,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: "transparent",
      whiteSpace: "normal",
      overflowWrap: "anywhere",
      textAlign: "center",
    }),
    [theme]
  );

  const TipLabel = useCallback(
    ({ text, tip }) => {
      return (
        <div
          style={labelHover}
          onMouseEnter={(e) => showTip(tip, e)}
          onMouseMove={moveTip}
          onMouseLeave={() => {
            if (tipHideTimer.current) window.clearTimeout(tipHideTimer.current);
            tipHideTimer.current = window.setTimeout(() => hideTip(), 30);
          }}
          onTouchStart={(e) => showTip(tip, e)}
          onTouchEnd={() => {
            if (tipHideTimer.current) window.clearTimeout(tipHideTimer.current);
            tipHideTimer.current = window.setTimeout(() => hideTip(), 1400);
          }}
          // no title attribute: prevents native browser tooltip regression
        >
          <span style={label}>{text}</span>
        </div>
      );
    },
    [hideTip, label, labelHover, moveTip, showTip]
  );

  const loadOpportunities = useCallback(async () => {
    setOppsLoading(true);
    setOppsErr("");
    try {
      const candidates = [
        "/api/opportunities?limit=2000&offset=0",
        "/api/opportunities?limit=500&offset=0",
        "/api/opportunities?limit=1000",
      ];

      let data = null;
      let lastErr = null;

      for (const url of candidates) {
        try {
          const res = await api.get(url);
          data = res.data;
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }

      if (lastErr && data == null) throw lastErr;

      const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      setOpps(arr);
    } catch (e) {
      setOppsErr(e?.response?.data?.detail || String(e?.message || "Failed to load opportunities"));
      setOpps([]);
    } finally {
      setOppsLoading(false);
    }
  }, []);

  const loadSalesOwners = useCallback(async () => {
    setOwnersLoading(true);
    setOwnersErr("");
    try {
      const { data } = await api.get("/api/resources?include_inactive=true&limit=500");
      const arr = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];

      const filtered = (arr || [])
        .filter((r) => r && r.active === true && r.is_sales === true)
        .sort((a, b) => resourceLabel(a).toLowerCase().localeCompare(resourceLabel(b).toLowerCase()));

      setSalesOwners(filtered);

      const chosen = toInt(ownerId);
      if (chosen && !filtered.some((r) => toInt(r?.id) === chosen)) setOwnerId("");
    } catch (e) {
      setOwnersErr(e?.response?.data?.detail || String(e?.message || "Failed to load sales owners"));
      setSalesOwners([]);
    } finally {
      setOwnersLoading(false);
    }
  }, [ownerId]);

  const tryLoadRegions = useCallback(async () => {
    setRegionsLoading(true);
    setRegionsSupported(false);
    setRegions([]);
    setRegionMapByResource({});

    try {
      const res = await api.get("/api/sales/regions?limit=500");
      const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.items) ? res.data.items : [];
      const clean = (list || [])
        .map((r) => ({ id: toInt(r?.id), name: String(r?.name || "").trim() }))
        .filter((r) => r.id && r.name)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

      setRegions(clean);
      setRegionsSupported(true);

      const map = {};
      for (const r of salesOwners || []) {
        const rid = toInt(r?.id);
        if (!rid) continue;

        const candidates = [
          `/api/sales/resources/${rid}/regions`,
          `/api/resources/${rid}/sales-regions`,
          `/api/resources/${rid}/regions`,
        ];

        let got = null;
        for (const url of candidates) {
          try {
            const rr = await api.get(url);
            got = rr.data;
            break;
          } catch {
            // ignore
          }
        }

        const arr = Array.isArray(got) ? got : Array.isArray(got?.items) ? got.items : [];
        const mapped = (arr || [])
          .map((x) => ({
            id: toInt(x?.id || x?.region_id || x?.regionId),
            name: String(x?.name || x?.region_name || x?.regionName || "").trim(),
          }))
          .filter((x) => x.id && x.name);

        if (mapped.length) map[String(rid)] = mapped;
      }

      setRegionMapByResource(map);

      const chosen = toInt(regionId);
      if (chosen && !clean.some((x) => toInt(x?.id) === chosen)) setRegionId("");
    } catch {
      setRegionsSupported(false);
      setRegions([]);
      setRegionMapByResource({});
      setRegionId("");
    } finally {
      setRegionsLoading(false);
    }
  }, [regionId, salesOwners]);

  useEffect(() => {
    loadOpportunities();
    loadSalesOwners();
  }, [loadOpportunities, loadSalesOwners]);

  useEffect(() => {
    if (!ownersLoading) tryLoadRegions();
  }, [ownersLoading, tryLoadRegions]);

  const ownerOptions = useMemo(() => {
    return (salesOwners || []).map((r) => ({ id: toInt(r?.id), label: resourceLabel(r) }));
  }, [salesOwners]);

  const stageOptions = useMemo(() => {
    const set = new Set();
    (opps || []).forEach((o) => {
      const s = String(o?.stage || "").trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [opps]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    (opps || []).forEach((o) => {
      const s = String(o?.status || "").trim();
      if (s) set.add(s);
    });
    const arr = Array.from(set).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    return arr.length ? arr : ["open", "won", "lost", "on_hold"];
  }, [opps]);

  const filteredOpps = useMemo(() => {
    const qq = normLower(q);
    const st = normLower(stage);
    const ss = normLower(status);
    const oid = toInt(ownerId);
    const rid = toInt(regionId);
    const fromN = dateNum(isoDateOnly(fromDate));
    const toN = dateNum(isoDateOnly(toDate));

    const ownerRegionsFor = (resourceId) => {
      const arr = regionMapByResource[String(resourceId)] || [];
      return arr.map((x) => toInt(x.id)).filter(Boolean);
    };

    return (opps || []).filter((o) => {
      if (!o) return false;

      if (ss && normLower(o.status) !== ss) return false;
      if (st && normLower(o.stage) !== st) return false;

      if (oid && toInt(o.owner_resource_id) !== oid) return false;

      if (regionsSupported && rid) {
        const x = toInt(o.owner_resource_id);
        if (!x) return false;
        const rs = ownerRegionsFor(x);
        if (!rs.includes(rid)) return false;
      }

      if (fromN || toN) {
        const d = isoDateOnly(o.expected_close_date);
        const dn = dateNum(d);
        if (!dn) return false;
        if (fromN && dn < fromN) return false;
        if (toN && dn > toN) return false;
      }

      if (qq) {
        const name = normLower(o.name);
        const desc = normLower(o.description || o.notes);
        const id = String(o.id || "");
        const cust = String(o.customer_id || "");
        const stageV = normLower(o.stage);
        const statusV = normLower(o.status);
        if (!name.includes(qq) && !desc.includes(qq) && !id.includes(qq) && !cust.includes(qq) && !stageV.includes(qq) && !statusV.includes(qq)) {
          return false;
        }
      }

      return true;
    });
  }, [opps, q, stage, status, ownerId, regionsSupported, regionId, fromDate, toDate, regionMapByResource]);

  const ownerNameById = useMemo(() => {
    const m = new Map();
    (salesOwners || []).forEach((r) => {
      const id = toInt(r?.id);
      if (!id) return;
      m.set(id, resourceLabel(r));
    });
    return m;
  }, [salesOwners]);

  const kpis = useMemo(() => {
    const rows = filteredOpps || [];
    let openCount = 0;
    let wonCount = 0;
    let lostCount = 0;

    let openAmount = 0;
    let wonAmount = 0;
    let lostAmount = 0;

    let pipelineTotal = 0;
    let pipelineWeighted = 0;

    rows.forEach((o) => {
      const amt = toNumber(o.amount) || 0;
      const prob = toNumber(o.probability);
      const prob01 = prob === null ? 0 : Math.max(0, Math.min(100, prob)) / 100;

      const st = normLower(o.status);

      pipelineTotal += amt;
      pipelineWeighted += amt * prob01;

      if (st === "won") {
        wonCount += 1;
        wonAmount += amt;
      } else if (st === "lost") {
        lostCount += 1;
        lostAmount += amt;
      } else {
        openCount += 1;
        openAmount += amt;
      }
    });

    return {
      totalCount: rows.length,
      pipelineTotal,
      pipelineWeighted,
      openCount,
      openAmount,
      wonCount,
      wonAmount,
      lostCount,
      lostAmount,
    };
  }, [filteredOpps]);

  const byStage = useMemo(() => {
    const rows = filteredOpps || [];
    const mAmt = sumBy(rows, (o) => String(o.stage || "—").trim() || "—", (o) => toNumber(o.amount) || 0);
    const mCnt = sumBy(rows, (o) => String(o.stage || "—").trim() || "—", () => 1);
    const out = Array.from(mAmt.entries()).map(([k, v]) => ({ stage: k, count: mCnt.get(k) || 0, amount: v }));
    out.sort((a, b) => b.amount - a.amount || a.stage.toLowerCase().localeCompare(b.stage.toLowerCase()));
    return out;
  }, [filteredOpps]);

  const byStatus = useMemo(() => {
    const rows = filteredOpps || [];
    const mAmt = sumBy(rows, (o) => String(o.status || "—").trim() || "—", (o) => toNumber(o.amount) || 0);
    const mCnt = sumBy(rows, (o) => String(o.status || "—").trim() || "—", () => 1);
    const out = Array.from(mAmt.entries()).map(([k, v]) => ({ status: k, count: mCnt.get(k) || 0, amount: v }));
    out.sort((a, b) => b.amount - a.amount || a.status.toLowerCase().localeCompare(b.status.toLowerCase()));
    return out;
  }, [filteredOpps]);

  const byOwner = useMemo(() => {
    const rows = filteredOpps || [];
    const mAmt = sumBy(rows, (o) => String(toInt(o.owner_resource_id) || 0), (o) => toNumber(o.amount) || 0);
    const mCnt = sumBy(rows, (o) => String(toInt(o.owner_resource_id) || 0), () => 1);

    const out = Array.from(mAmt.entries()).map(([k, v]) => {
      const id = Number(k) || 0;
      const label = id ? ownerNameById.get(id) || `Resource #${id}` : "Unassigned";
      return { owner_id: id || null, owner: label, count: mCnt.get(k) || 0, amount: v };
    });

    out.sort((a, b) => b.amount - a.amount || a.owner.toLowerCase().localeCompare(b.owner.toLowerCase()));
    return out;
  }, [filteredOpps, ownerNameById]);

  const byRegion = useMemo(() => {
    if (!regionsSupported) return [];
    const rows = filteredOpps || [];

    const resourceRegions = (resourceId) => {
      const arr = regionMapByResource[String(resourceId)] || [];
      return arr.map((x) => ({ id: toInt(x.id), name: x.name })).filter((x) => x.id && x.name);
    };

    const bucket = new Map();
    rows.forEach((o) => {
      const rid = toInt(o.owner_resource_id);
      const amt = toNumber(o.amount) || 0;

      const rs = rid ? resourceRegions(rid) : [];
      if (!rs.length) {
        const k = "Unassigned";
        const cur = bucket.get(k) || { count: 0, amount: 0 };
        bucket.set(k, { count: cur.count + 1, amount: cur.amount + amt });
        return;
      }

      const split = amt / rs.length;
      rs.forEach((r) => {
        const k = r.name;
        const cur = bucket.get(k) || { count: 0, amount: 0 };
        bucket.set(k, { count: cur.count + 1, amount: cur.amount + split });
      });
    });

    const out = Array.from(bucket.entries()).map(([name, v]) => ({ region: name, count: v.count, amount: v.amount }));
    out.sort((a, b) => b.amount - a.amount || a.region.toLowerCase().localeCompare(b.region.toLowerCase()));
    return out;
  }, [filteredOpps, regionsSupported, regionMapByResource]);

  const clearFilters = () => {
    setQ("");
    setStatus("");
    setStage("");
    setOwnerId("");
    setRegionId("");
    setFromDate("");
    setToDate("");
  };

  return (
    <div
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      style={wrap}
    >
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2.5">
        <button
          type="button"
          className="min-h-[44px] w-full bg-btnGeneralNormal text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
          style={{
            borderRadius: 10,
            padding: "8px 10px",
            fontWeight: 900,
            fontSize: 12,
          }}
          onClick={clearFilters}
        >
          Reset
        </button>

        <button
          type="button"
          className="min-h-[44px] w-full bg-btnActionNormal text-white hover:bg-btnActionHover active:bg-btnActionHover sm:w-auto"
          style={{
            borderRadius: 10,
            padding: "8px 10px",
            fontWeight: 900,
            fontSize: 12,
            opacity: oppsLoading ? 0.6 : 1,
          }}
          onClick={loadOpportunities}
          disabled={oppsLoading}
        >
          {oppsLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {oppsErr ? (
        <div
          className="mt-2.5 break-words"
          style={{
            fontSize: 12,
            color: theme?.danger || "#b91c1c",
            overflowWrap: "anywhere",
          }}
        >
          {oppsErr}
        </div>
      ) : null}

      {ownersErr ? (
        <div
          className="mt-2.5 break-words"
          style={{
            fontSize: 12,
            color: theme?.danger || "#b91c1c",
            overflowWrap: "anywhere",
          }}
        >
          {ownersErr}
        </div>
      ) : null}

      {/* Filters */}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1.2fr_1fr_1fr]">
        <div className="min-w-0">
          <TipLabel
            text="Search"
            tip={
              "Filters current list client-side.\nMatches name, description, id, stage, status."
            }
          />
          <input
            style={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, status, stage, id…"
          />
        </div>

        <div className="min-w-0">
          <TipLabel
            text="Status"
            tip={
              "Filters by opportunity status.\nValues are whatever exists in your DB."
            }
          />
          <select
            style={input}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">— Any —</option>
            {(statusOptions || []).map((s) => (
              <option key={s} value={s}>
                {String(s)
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-0">
          <TipLabel
            text="Stage"
            tip={
              "Filters by opportunity stage.\nStage list is derived from live opportunities."
            }
          />
          <select
            style={input}
            value={stage}
            onChange={(e) => setStage(e.target.value)}
          >
            <option value="">— Any —</option>
            {(stageOptions || []).map((s) => (
              <option key={s} value={s}>
                {String(s)
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="min-w-0">
          <TipLabel
            text="Owner"
            tip={
              "Sales owners only.\nFiltered to active resources with is_sales=true."
            }
          />
          <select
            style={input}
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            disabled={ownersLoading}
          >
            <option value="">
              {ownersLoading ? "Loading..." : "— Any —"}
            </option>

            {(ownerOptions || []).length === 0 && !ownersLoading ? (
              <option value="" disabled>
                No sales owners found
              </option>
            ) : null}

            {(ownerOptions || []).map((o) => (
              <option key={String(o.id)} value={String(o.id)}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-0">
          <TipLabel
            text="Expected Close From"
            tip={
              "Filters by expected_close_date.\nInclusive lower bound."
            }
          />
          <input
            type="date"
            style={input}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>

        <div className="min-w-0">
          <TipLabel
            text="Expected Close To"
            tip={
              "Filters by expected_close_date.\nInclusive upper bound."
            }
          />
          <input
            type="date"
            style={input}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
      </div>

      {regionsSupported ? (
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_2fr]">
          <div className="min-w-0">
            <TipLabel
              text="Region"
              tip={
                "By Region filtering.\nRequires backend region endpoints."
              }
            />
            <select
              style={input}
              value={regionId}
              onChange={(e) => setRegionId(e.target.value)}
              disabled={regionsLoading}
            >
              <option value="">
                {regionsLoading ? "Loading..." : "— Any —"}
              </option>

              {(regions || []).map((r) => (
                <option key={String(r.id)} value={String(r.id)}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex min-w-0 flex-col items-start gap-2 md:flex-row md:flex-wrap md:items-end md:gap-2.5">
            <span style={pill}>Region filtering enabled</span>
            <span
              className="break-words text-[11px]"
              style={{
                opacity: 0.65,
                overflowWrap: "anywhere",
              }}
            >
              If a resource has multiple regions, the region breakdown splits
              amount evenly to avoid double counting.
            </span>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex min-w-0 flex-col items-start gap-2 md:flex-row md:flex-wrap md:items-center md:gap-2.5">
          <span style={pill}>Region filtering disabled</span>
          <span
            className="break-words text-[11px]"
            style={{
              opacity: 0.65,
              overflowWrap: "anywhere",
            }}
          >
            Enable backend endpoints for regions to unlock “By Region”
            filtering + breakdown.
          </span>
        </div>
      )}

      {/* KPIs (Won/Lost card removed) */}
      <div className="mt-3.5 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div
          className="min-w-0"
          style={{
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 12,
            padding: 12,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              cursor: "default",
            }}
            onMouseEnter={(e) =>
              showTip(
                "Sum of amounts across the current filtered set.",
                e
              )
            }
            onMouseMove={moveTip}
            onMouseLeave={hideTip}
            onTouchStart={(e) =>
              showTip(
                "Sum of amounts across the current filtered set.",
                e
              )
            }
          >
            <div style={label}>Pipeline Total</div>
          </div>

          <div
            className="break-words"
            style={{
              fontSize: 18,
              fontWeight: 950,
              marginTop: 6,
              overflowWrap: "anywhere",
            }}
          >
            {moneyFmt(kpis.pipelineTotal)}
          </div>
        </div>

        <div
          className="min-w-0"
          style={{
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 12,
            padding: 12,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              cursor: "default",
            }}
            onMouseEnter={(e) =>
              showTip(
                "Amount × probability%.\nNull probability is treated as 0.",
                e
              )
            }
            onMouseMove={moveTip}
            onMouseLeave={hideTip}
            onTouchStart={(e) =>
              showTip(
                "Amount × probability%.\nNull probability is treated as 0.",
                e
              )
            }
          >
            <div style={label}>Weighted Pipeline</div>
          </div>

          <div
            className="break-words"
            style={{
              fontSize: 18,
              fontWeight: 950,
              marginTop: 6,
              overflowWrap: "anywhere",
            }}
          >
            {moneyFmt(kpis.pipelineWeighted)}
          </div>
        </div>

        <div
          className="min-w-0"
          style={{
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 12,
            padding: 12,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              cursor: "default",
            }}
            onMouseEnter={(e) =>
              showTip(
                "Everything not won/lost.\nCount and total amount (filtered set).",
                e
              )
            }
            onMouseMove={moveTip}
            onMouseLeave={hideTip}
            onTouchStart={(e) =>
              showTip(
                "Everything not won/lost.\nCount and total amount (filtered set).",
                e
              )
            }
          >
            <div style={label}>Open</div>
          </div>

          <div
            className="break-words"
            style={{
              fontSize: 18,
              fontWeight: 950,
              marginTop: 6,
              overflowWrap: "anywhere",
            }}
          >
            {kpis.openCount} · {moneyFmt(kpis.openAmount)}
          </div>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="mt-3.5 grid min-h-0 grid-cols-1 gap-3 md:grid-cols-2">
        <div
          className="min-h-0 min-w-0 overflow-visible md:overflow-hidden"
          style={{
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 12,
            padding: 12,
          }}
        >
          <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2.5">
            <div style={{ fontSize: 12, fontWeight: 950, opacity: 0.9 }}>
              By Stage
            </div>
            <div style={{ fontSize: 11, opacity: 0.65 }}>
              {filteredOpps.length} opps
            </div>
          </div>

          <div className="mt-2.5 overflow-visible md:max-h-[240px] md:overflow-auto">
            {byStage.length === 0 ? (
              <div style={{ fontSize: 12, opacity: 0.7 }}>No data.</div>
            ) : (
              <table
                className="block w-full md:table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead className="hidden md:table-header-group">
                  <tr
                    style={{
                      textAlign: "left",
                      fontSize: 11,
                      opacity: 0.7,
                    }}
                  >
                    <th style={{ padding: "6px 0" }}>Stage</th>
                    <th style={{ padding: "6px 0" }}>Count</th>
                    <th style={{ padding: "6px 0", textAlign: "right" }}>
                      Amount
                    </th>
                  </tr>
                </thead>

                <tbody className="block space-y-2 md:table-row-group md:space-y-0">
                  {byStage.map((r) => (
                    <tr
                      key={r.stage}
                      className="block overflow-hidden rounded-lg border border-gray-200 bg-white md:table-row md:rounded-none md:border-0 md:bg-transparent"
                      style={{
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-3 py-2.5 md:table-cell md:border-b-0 md:px-0 md:py-2">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Stage
                        </span>
                        <span
                          className="min-w-0 flex-1 break-words text-right md:text-left"
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {String(r.stage)
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      </td>

                      <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-3 py-2.5 md:table-cell md:border-b-0 md:px-0 md:py-2">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Count
                        </span>
                        <span className="text-right text-xs md:text-left">
                          {r.count}
                        </span>
                      </td>

                      <td className="flex items-start justify-between gap-4 px-3 py-2.5 md:table-cell md:px-0 md:py-2 md:text-right">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Amount
                        </span>
                        <span
                          className="min-w-0 flex-1 break-words text-right"
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {moneyFmt(r.amount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div
          className="min-h-0 min-w-0 overflow-visible md:overflow-hidden"
          style={{
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 12,
            padding: 12,
          }}
        >
          <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2.5">
            <div style={{ fontSize: 12, fontWeight: 950, opacity: 0.9 }}>
              By Status
            </div>
            <div
              className="break-words"
              style={{
                fontSize: 11,
                opacity: 0.65,
                overflowWrap: "anywhere",
              }}
            >
              Weighted: {moneyFmt(kpis.pipelineWeighted)}
            </div>
          </div>

          <div className="mt-2.5 overflow-visible md:max-h-[240px] md:overflow-auto">
            {byStatus.length === 0 ? (
              <div style={{ fontSize: 12, opacity: 0.7 }}>No data.</div>
            ) : (
              <table
                className="block w-full md:table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead className="hidden md:table-header-group">
                  <tr
                    style={{
                      textAlign: "left",
                      fontSize: 11,
                      opacity: 0.7,
                    }}
                  >
                    <th style={{ padding: "6px 0" }}>Status</th>
                    <th style={{ padding: "6px 0" }}>Count</th>
                    <th style={{ padding: "6px 0", textAlign: "right" }}>
                      Amount
                    </th>
                  </tr>
                </thead>

                <tbody className="block space-y-2 md:table-row-group md:space-y-0">
                  {byStatus.map((r) => (
                    <tr
                      key={r.status}
                      className="block overflow-hidden rounded-lg border border-gray-200 bg-white md:table-row md:rounded-none md:border-0 md:bg-transparent"
                      style={{
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-3 py-2.5 md:table-cell md:border-b-0 md:px-0 md:py-2">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Status
                        </span>
                        <span
                          className="min-w-0 flex-1 break-words text-right md:text-left"
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {String(r.status)
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      </td>

                      <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-3 py-2.5 md:table-cell md:border-b-0 md:px-0 md:py-2">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Count
                        </span>
                        <span className="text-right text-xs md:text-left">
                          {r.count}
                        </span>
                      </td>

                      <td className="flex items-start justify-between gap-4 px-3 py-2.5 md:table-cell md:px-0 md:py-2 md:text-right">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Amount
                        </span>
                        <span
                          className="min-w-0 flex-1 break-words text-right"
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {moneyFmt(r.amount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div
        className="mt-3 min-h-0 min-w-0 overflow-visible md:overflow-hidden"
        style={{
          border: "1px solid rgba(0,0,0,0.06)",
          borderRadius: 12,
          padding: 12,
        }}
      >
        <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2.5">
          <div style={{ fontSize: 12, fontWeight: 950, opacity: 0.9 }}>
            By Owner
          </div>

          <div
            className="break-words"
            style={{
              fontSize: 11,
              opacity: 0.65,
              overflowWrap: "anywhere",
            }}
          >
            Total: {moneyFmt(kpis.pipelineTotal)} · Avg Prob:{" "}
            {pctFmt(
              filteredOpps.length
                ? filteredOpps.reduce(
                    (a, o) => a + (toNumber(o?.probability) || 0),
                    0
                  ) / filteredOpps.length
                : null
            )}
          </div>
        </div>

        <div className="mt-2.5 overflow-visible md:max-h-[260px] md:overflow-auto">
          {byOwner.length === 0 ? (
            <div style={{ fontSize: 12, opacity: 0.7 }}>No data.</div>
          ) : (
            <table
              className="block w-full md:table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead className="hidden md:table-header-group">
                <tr
                  style={{
                    textAlign: "left",
                    fontSize: 11,
                    opacity: 0.7,
                  }}
                >
                  <th style={{ padding: "6px 0" }}>Owner</th>
                  <th style={{ padding: "6px 0" }}>Count</th>
                  <th style={{ padding: "6px 0", textAlign: "right" }}>
                    Amount
                  </th>
                </tr>
              </thead>

              <tbody className="block space-y-2 md:table-row-group md:space-y-0">
                {byOwner.map((r) => (
                  <tr
                    key={String(r.owner_id || "none")}
                    className="block overflow-hidden rounded-lg border border-gray-200 bg-white md:table-row md:rounded-none md:border-0 md:bg-transparent"
                    style={{
                      borderTop: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-3 py-2.5 md:table-cell md:border-b-0 md:px-0 md:py-2">
                      <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                        Owner
                      </span>
                      <span
                        className="min-w-0 flex-1 break-words text-right md:text-left"
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {r.owner}
                      </span>
                    </td>

                    <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-3 py-2.5 md:table-cell md:border-b-0 md:px-0 md:py-2">
                      <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                        Count
                      </span>
                      <span className="text-right text-xs md:text-left">
                        {r.count}
                      </span>
                    </td>

                    <td className="flex items-start justify-between gap-4 px-3 py-2.5 md:table-cell md:px-0 md:py-2 md:text-right">
                      <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                        Amount
                      </span>
                      <span
                        className="min-w-0 flex-1 break-words text-right"
                        style={{
                          fontSize: 12,
                          fontWeight: 900,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {moneyFmt(r.amount)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {regionsSupported ? (
        <div
          className="mt-3 min-h-0 min-w-0 overflow-visible md:overflow-hidden"
          style={{
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 12,
            padding: 12,
          }}
        >
          <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2.5">
            <div style={{ fontSize: 12, fontWeight: 950, opacity: 0.9 }}>
              By Region
            </div>
            <div style={{ fontSize: 11, opacity: 0.65 }}>Enabled</div>
          </div>

          <div className="mt-2.5 overflow-visible md:max-h-[220px] md:overflow-auto">
            {byRegion.length === 0 ? (
              <div
                className="break-words"
                style={{
                  fontSize: 12,
                  opacity: 0.7,
                  overflowWrap: "anywhere",
                }}
              >
                No region data (assign regions to sales resources).
              </div>
            ) : (
              <table
                className="block w-full md:table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <thead className="hidden md:table-header-group">
                  <tr
                    style={{
                      textAlign: "left",
                      fontSize: 11,
                      opacity: 0.7,
                    }}
                  >
                    <th style={{ padding: "6px 0" }}>Region</th>
                    <th style={{ padding: "6px 0" }}>Count</th>
                    <th style={{ padding: "6px 0", textAlign: "right" }}>
                      Amount
                    </th>
                  </tr>
                </thead>

                <tbody className="block space-y-2 md:table-row-group md:space-y-0">
                  {byRegion.map((r) => (
                    <tr
                      key={r.region}
                      className="block overflow-hidden rounded-lg border border-gray-200 bg-white md:table-row md:rounded-none md:border-0 md:bg-transparent"
                      style={{
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-3 py-2.5 md:table-cell md:border-b-0 md:px-0 md:py-2">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Region
                        </span>
                        <span
                          className="min-w-0 flex-1 break-words text-right md:text-left"
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {r.region}
                        </span>
                      </td>

                      <td className="flex items-start justify-between gap-4 border-b border-gray-100 px-3 py-2.5 md:table-cell md:border-b-0 md:px-0 md:py-2">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Count
                        </span>
                        <span className="text-right text-xs md:text-left">
                          {r.count}
                        </span>
                      </td>

                      <td className="flex items-start justify-between gap-4 px-3 py-2.5 md:table-cell md:px-0 md:py-2 md:text-right">
                        <span className="shrink-0 font-semibold text-gray-500 md:hidden">
                          Amount
                        </span>
                        <span
                          className="min-w-0 flex-1 break-words text-right"
                          style={{
                            fontSize: 12,
                            fontWeight: 900,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {moneyFmt(r.amount)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      <TooltipPortal
        open={tipOpen}
        x={tipXY.x}
        y={tipXY.y}
        text={tipText}
        theme={theme}
      />
    </div>
  );
}
